import type { ApiError } from '@/lib/api'

/**
 * The arithmetic and the wording of the detection step, kept apart from the
 * screen so it can be tested without a click — and so a change to a stage
 * boundary is a change to one readable function rather than to JSX.
 */

const STEPS = ['transfer', 'detect', 'read', 'result'] as const
export type StepKey = (typeof STEPS)[number]
export type StepState = 'pending' | 'running' | 'done'

export interface Progress {
  value: number
  determinate: boolean
}

/**
 * Where the bar stands, and whether that position means anything.
 *
 * Each of the four stages owns a quarter. Inside a stage the bar only moves
 * on a number the server actually gave us: the uploaded bytes, and the job's
 * own percentage. Detection has neither, so the bar holds at the stage
 * boundary and says so by being indeterminate — it never drifts forward on a
 * timer.
 *
 * `readPercent` is the job's `progress` field, 0-100. It is the only figure
 * `GET /v1/jobs/:jobId` reports — the page count the checklist would rather
 * name is not in that response at all.
 */
export function funnelProgress(input: {
  step: StepKey
  transferPercent: number
  readPercent?: number
}): Progress {
  const index = STEPS.indexOf(input.step)
  const base = index / STEPS.length
  const slice = 1 / STEPS.length

  if (input.step === 'transfer') {
    const fraction = Math.min(Math.max(input.transferPercent, 0), 100) / 100
    return { value: base + fraction * slice, determinate: true }
  }
  if (input.step === 'read' && input.readPercent != null) {
    const fraction = Math.min(Math.max(input.readPercent, 0), 100) / 100
    return { value: base + fraction * slice, determinate: true }
  }
  if (input.step === 'result') return { value: 1, determinate: true }
  return { value: base, determinate: false }
}

/**
 * The checklist's four markers.
 *
 * Detection keeps spinning until the reveal has finished counting: the work
 * is done, but the number on screen is not yet the number that came back, and
 * a green tick beside a still-climbing count reads as a finished count.
 */
export function checklistStates(step: StepKey, revealDone: boolean): Record<StepKey, StepState> {
  const current = STEPS.indexOf(step)
  const states = {} as Record<StepKey, StepState>
  STEPS.forEach((key, index) => {
    states[key] = index < current ? 'done' : index === current ? 'running' : 'pending'
  })
  if (states.detect === 'done' && !revealDone) states.detect = 'running'
  return states
}

/** German file size, one decimal, for a line the person reads rather than parses. */
export function sizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} kB`
}

/**
 * What the detection line says.
 *
 * Before the response there is no count to give, so it does not pretend to
 * have one. After it, the number climbs to a total that is already known.
 */
export function detectDetail(
  state: StepState,
  fieldTotal: number | null,
  revealed: number,
  converting: boolean,
): string | undefined {
  if (converting) return 'Seiten werden erzeugt …'
  if (fieldTotal == null) return state === 'running' ? 'Die Seite wird gelesen …' : undefined
  if (fieldTotal === 0) return 'Kein Feld erkannt'
  const count = state === 'running' ? revealed : fieldTotal
  return count === 1 ? '1 Feld gefunden' : `${count} Felder gefunden`
}

export function liveSummary(
  step: StepKey,
  states: Record<StepKey, StepState>,
  fieldTotal: number | null,
): string {
  if (states.transfer === 'running') return 'Schritt 1 von 4: Die Datei wird übertragen.'
  if (states.detect === 'running') {
    return fieldTotal == null
      ? 'Schritt 2 von 4: Die Felder werden erkannt.'
      : `Schritt 2 von 4: ${fieldTotal} Felder erkannt.`
  }
  if (step === 'read') return 'Schritt 3 von 4: Die Werte werden gelesen.'
  return 'Schritt 4 von 4: Das Ergebnis ist fertig.'
}

/**
 * A timeout is not an ordinary network failure.
 *
 * Detection runs long enough that axios' own deadline (five minutes) and the
 * ingress' 300 s can both fire on a request the gateway is still finishing —
 * so the template may exist. Offering "noch einmal versuchen" as the first
 * answer would quietly create a second one.
 */
export function timedOut(error: ApiError): boolean {
  const cause = error.cause as { code?: string } | undefined
  return (
    error.code === 'network_error' &&
    (cause?.code === 'ECONNABORTED' || cause?.code === 'ETIMEDOUT')
  )
}

/**
 * The quota refusal in the funnel's voice.
 *
 * `limitMessage` in components/PlanQuota says the same thing in "Sie", for
 * the signed-in app. The pre-account funnel says "du" throughout, and a
 * single sentence switching register mid-flow is more jarring than two
 * copies of one message.
 */
export function limitCopy(
  code: 'page_limit_reached' | 'template_limit_reached',
  used: number,
  limit: number,
): { title: string; body: string } {
  if (code === 'template_limit_reached') {
    return {
      title: 'Dein Tarif enthält ein Formular',
      body: `Du hast ${used} von ${limit} Formularen eingerichtet. Lösche ein bestehendes oder hol dir Guthaben für mehr.`,
    }
  }
  return {
    title: 'Dein Kontingent ist aufgebraucht',
    body: `${used} von ${limit} Seiten verbraucht. Alles, was du schon hochgeladen hast, bleibt nutzbar — lesen, prüfen und exportieren geht weiter.`,
  }
}
